/**
 * navEngine — the math behind live navigation.
 *
 * Pure functions, no React, no map SDK. Given a route and a GPS position,
 * it answers: where am I on the route, which maneuver am I in, how far to
 * the next turn, how far to the next charger, what heading am I travelling.
 *
 * Kept pure so it's unit-testable without a browser or a map.
 *
 * Coordinate convention: every point is [lng, lat] (GeoJSON order), matching
 * what processValhallaResponse produces in geometry_full.
 */

const EARTH_R_KM = 6371;

// ---------------------------------------------------------------------------
// GEO PRIMITIVES
// ---------------------------------------------------------------------------

/** Great-circle distance between two [lng,lat] points, in km. */
export function haversineKm([lng1, lat1], [lng2, lat2]) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R_KM * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Compass bearing from point A to point B, in degrees (0=N, 90=E).
 * Used to rotate the map "heading-up" — the direction of travel.
 */
export function bearingDeg([lng1, lat1], [lng2, lat2]) {
  const y = Math.sin(toRad(lng2 - lng1)) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lng2 - lng1));
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function toRad(d) { return d * Math.PI / 180; }
function toDeg(r) { return r * 180 / Math.PI; }

// ---------------------------------------------------------------------------
// ROUTE INDEXING
// ---------------------------------------------------------------------------

/**
 * Pre-compute cumulative distance along a route polyline.
 * Returns an array `cum` where cum[i] = km from route start to point i.
 * Done once when navigation begins; every position update reuses it.
 */
export function buildRouteIndex(geometry) {
  const cum = [0];
  for (let i = 1; i < geometry.length; i++) {
    cum.push(cum[i - 1] + haversineKm(geometry[i - 1], geometry[i]));
  }
  return cum;
}

/**
 * Snap a raw GPS position to the nearest point on the route.
 *
 * GPS is noisy and the driver isn't exactly on the polyline, so we find the
 * closest route vertex and report:
 *   - snapped index into the geometry
 *   - distance travelled from route start (km)
 *   - off-route distance (km) — how far the raw GPS is from the route
 *
 * `hintIndex` lets us search near the last known position instead of the
 * whole route — much faster, and avoids snapping back to an earlier point
 * where the route crosses itself.
 */
export function snapToRoute(geometry, routeIndex, position, hintIndex = 0) {
  const window = 400; // search +/- this many vertices around the hint
  const start = Math.max(0, hintIndex - 50);
  const end = Math.min(geometry.length - 1, hintIndex + window);

  let bestIdx = start;
  let bestDist = Infinity;
  for (let i = start; i <= end; i++) {
    const d = haversineKm(geometry[i], position);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return {
    index: bestIdx,
    distance_along_km: routeIndex[bestIdx],
    off_route_km: bestDist,
  };
}

// ---------------------------------------------------------------------------
// MANEUVER TRACKING
// ---------------------------------------------------------------------------

/**
 * Given the snapped position index, find the active maneuver and the next one.
 *
 * A maneuver spans [begin_shape_index, end_shape_index] along the route. The
 * "active" maneuver is the one whose span contains the driver. The "next"
 * maneuver is the upcoming turn the driver is approaching.
 *
 * Returns:
 *   active_index   — index into the maneuvers array of the current step
 *   next_index     — index of the upcoming turn (active_index + 1, usually)
 *   dist_to_next_km — distance from current position to the next turn
 */
export function trackManeuver(maneuvers, routeIndex, snappedIndex) {
  if (!Array.isArray(maneuvers) || maneuvers.length === 0) {
    return { active_index: 0, next_index: 0, dist_to_next_km: 0 };
  }
  // Find the maneuver whose shape span contains snappedIndex
  let activeIdx = 0;
  for (let i = 0; i < maneuvers.length; i++) {
    const m = maneuvers[i];
    if (snappedIndex >= m.begin_shape_index) {
      activeIdx = i;
    } else {
      break;
    }
  }
  const nextIdx = Math.min(activeIdx + 1, maneuvers.length - 1);
  // Distance to the next maneuver = distance from here to where it begins
  const nextBeginIdx = maneuvers[nextIdx]?.begin_shape_index ?? snappedIndex;
  const distToNext = Math.max(
    0,
    (routeIndex[nextBeginIdx] ?? 0) - (routeIndex[snappedIndex] ?? 0)
  );
  return {
    active_index: activeIdx,
    next_index: nextIdx,
    dist_to_next_km: distToNext,
  };
}

// ---------------------------------------------------------------------------
// CHARGER COUNTDOWN
// ---------------------------------------------------------------------------

/**
 * Find the next charging stop ahead of the driver and the distance to it.
 *
 * `stops` is the charging-plan array (each stop carries its distance from
 * route origin). As the driver advances, stops fall behind; this returns
 * the first one still ahead, with a live-updating distance.
 *
 * Returns null when there are no stops ahead (or the station database isn't
 * populated yet — see Task F). Callers should handle null gracefully.
 */
export function nextChargerAhead(stops, distanceAlongKm) {
  if (!Array.isArray(stops) || stops.length === 0) return null;
  let best = null;
  for (const s of stops) {
    const stopKm = s.distance_from_origin_km;
    if (typeof stopKm !== 'number') continue;
    if (stopKm > distanceAlongKm + 0.05) {  // 50m past = "reached"
      if (!best || stopKm < best.distance_from_origin_km) best = s;
    }
  }
  if (!best) return null;
  return {
    stop: best,
    distance_km: best.distance_from_origin_km - distanceAlongKm,
  };
}

// ---------------------------------------------------------------------------
// DYNAMIC CAMERA
// ---------------------------------------------------------------------------

/**
 * Pick a map zoom level based on context: wide on open highway, tight when a
 * turn is coming up. This is what makes the camera feel like a real nav
 * system rather than a static map.
 *
 * dist_to_next_km — distance to the next maneuver
 * speed_kmh       — current speed (faster = look further ahead = zoom out)
 */
export function dynamicZoom(distToNextKm, speedKmh) {
  // Approaching a turn — zoom in tight so the driver sees the junction.
  if (distToNextKm < 0.3) return 17.5;
  if (distToNextKm < 0.8) return 16.5;
  if (distToNextKm < 2)   return 15.5;
  // Cruising — zoom reflects speed so there's enough road shown ahead.
  if (speedKmh > 80) return 13.5;
  if (speedKmh > 50) return 14.5;
  return 15;
}

/**
 * Whole-snapshot navigation state for one GPS update. This is the single
 * function the UI calls each tick — it ties the pieces above together.
 *
 *   route        — processValhallaResponse output (geometry_full + maneuvers)
 *   routeIndex   — buildRouteIndex(route.geometry_full), computed once
 *   position     — [lng, lat] current GPS
 *   prevPosition — [lng, lat] previous GPS (for heading); may be null
 *   stops        — charging stops array (may be empty)
 *   hintIndex    — last snapped index, for fast searching
 */
export function computeNavState(route, routeIndex, position, prevPosition, stops, hintIndex) {
  const geometry = route.geometry_full || route.geometry_simplified || [];
  const snap = snapToRoute(geometry, routeIndex, position, hintIndex);
  const man = trackManeuver(route.maneuvers || [], routeIndex, snap.index);
  const charger = nextChargerAhead(stops || [], snap.distance_along_km);

  // Heading: prefer movement-derived bearing; fall back to route direction.
  let heading = 0;
  if (prevPosition && haversineKm(prevPosition, position) > 0.003) {
    heading = bearingDeg(prevPosition, position);
  } else if (geometry[snap.index] && geometry[snap.index + 1]) {
    heading = bearingDeg(geometry[snap.index], geometry[snap.index + 1]);
  }

  const totalKm = routeIndex[routeIndex.length - 1] || 0;
  return {
    snapped_index: snap.index,
    distance_along_km: snap.distance_along_km,
    distance_remaining_km: Math.max(0, totalKm - snap.distance_along_km),
    off_route_km: snap.off_route_km,
    active_maneuver_index: man.active_index,
    next_maneuver_index: man.next_index,
    dist_to_next_maneuver_km: man.dist_to_next_km,
    next_charger: charger,        // { stop, distance_km } or null
    heading_deg: heading,
    progress_pct: totalKm > 0 ? (snap.distance_along_km / totalKm) * 100 : 0,
  };
}
